import { t, Trans } from '@lingui/macro';
import { TireQuoteItem } from 'noddi-types';
import { NoddiButton } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiChip, NoddiDropdown, TireBaseInfo } from 'noddi-ui-web';

import { customerRoutes, formatCurrencyAmount, getTirePrice } from 'noddi-util';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTireOfferStore } from '../../stores/tireOfferStore';
import { getQuantityOptionsWithTrans } from '../../utils/translation';
import { ErrorPageWithTranslations } from '../ErrorPageWithTrans';
import { StaggeredTireSelectionCard } from './StaggeredTireSelectionCard';
import { TireMetaData } from './TireMetaData';

interface TireSelectionCardProps {
  quoteItem: TireQuoteItem;
  slug: string;
  index: number;
}

enum TireClassification {
  ECONOMY = 'ECONOMY',
  PREMIUM = 'PREMIUM',
  QUALITY = 'QUALITY'
}

const getClassification = (classification: string) => {
  switch (classification) {
    case TireClassification.ECONOMY:
      return t`Economy`;
    case TireClassification.PREMIUM:
      return t`Premium`;
    case TireClassification.QUALITY:
      return t`Standard`;
    default:
      return undefined;
  }
};

export const TireSelectionCard = ({ quoteItem, slug, index }: TireSelectionCardProps) => {
  const isStaggered = quoteItem.quoteItemTires.length === 2;

  if (quoteItem.quoteItemTires.length === 0) {
    return <ErrorPageWithTranslations />;
  }

  const classification = quoteItem.quoteItemTires[0]?.tireInventory.tire.classification
    ? getClassification(quoteItem.quoteItemTires[0].tireInventory.tire.classification)
    : undefined;

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between gap-4'>
        <h6 className='font-bold text-5 text-darkPurple'>{`Alt. ${index + 1}`}</h6>
        <div className='flex gap-2'>
          {quoteItem.isRecommended && <NoddiChip variant='success' label={t`We recommend`} size='small' />}
          {classification && <NoddiChip size='small' variant='neutral' label={classification} />}
        </div>
      </div>

      {isStaggered ? (
        <StaggeredTireSelectionCard quoteItem={quoteItem} slug={slug} />
      ) : (
        <SingleTireSelectionCard quoteItem={quoteItem} slug={slug} />
      )}
    </div>
  );
};

interface SingleTireSelectionCardProps {
  quoteItem: TireQuoteItem;
  slug: string;
}

const SingleTireSelectionCard = ({ quoteItem, slug }: SingleTireSelectionCardProps) => {
  const { setSelectedQuoteItem } = useTireOfferStore();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(quoteItem.quoteItemTires[0]?.quantityDefault ?? 0);

  const tireOption = quoteItem.quoteItemTires[0]!;
  const quantityOptions = getQuantityOptionsWithTrans(4);

  const onSelectTireOption = () => {
    const updatedQuoteItem = {
      ...quoteItem,
      quoteItemTires: quoteItem.quoteItemTires.map((tire) => ({
        ...tire,
        quantitySelected: quantity
      }))
    };
    setSelectedQuoteItem(updatedQuoteItem);
    navigate(customerRoutes.tireOfferSummary.getPath({ slug }));
  };

  const price = getTirePrice(tireOption);

  return (
    <NoddiBasicCard className='flex flex-col gap-3'>
      <TireBaseInfo tire={tireOption.tireInventory.tire} />
      <TireMetaData tire={tireOption.tireInventory.tire} />
      <div className='flex flex-col items-end justify-between gap-2'>
        <div className='flex w-full flex-col'>
          <div className='flex justify-between gap-4'>
            <Trans>Per tire</Trans>
            <span>
              {formatCurrencyAmount({
                money: price,
                decimals: 0
              })}
            </span>
          </div>
          <div className='flex justify-between gap-4 font-bold'>
            <span>{t`Total`}</span>
            <span>
              {formatCurrencyAmount({
                money: { amount: price.amount * quantity, currency: price.currency },
                decimals: 0
              })}
            </span>
          </div>
        </div>
      </div>
      <div className='flex w-full justify-end gap-4'>
        <NoddiDropdown
          className='flex min-w-25'
          options={quantityOptions}
          onSelect={(item) => setQuantity(item.value)}
          placeholder={`${quantity} ${t`pcs`}`}
        />
        <NoddiButton className='flex flex-1' onPress={onSelectTireOption}>{t`Select`}</NoddiButton>
      </div>
    </NoddiBasicCard>
  );
};
